import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { PackageX, PackagePlus } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import {
  AdminDataTable,
  type ColumnDef,
  EmptyState,
} from "@/components/admin/shared";
import { RestockModal } from "./RestockModal";
import { useInventoryStock } from "@/hooks/admin/useInventory";
import type { InventoryProduct } from "@/lib/admin-api/inventory";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAGE_SIZE = 20;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OutOfStockTab() {
  const { t } = useTranslation("admin");

  const [page, setPage] = useState(1);
  const [restockProduct, setRestockProduct] =
    useState<InventoryProduct | null>(null);

  const queryParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      status: "out_of_stock" as const,
    }),
    [page],
  );

  const { data, isLoading, isError } = useInventoryStock(queryParams);

  const handleRestock = useCallback((product: InventoryProduct) => {
    setRestockProduct(product);
  }, []);

  const handleCloseRestock = useCallback(() => {
    setRestockProduct(null);
  }, []);

  const products = data?.products ?? [];
  const pagination = data?.pagination;

  const columns: readonly ColumnDef<InventoryProduct>[] = useMemo(
    () => [
      {
        id: "product",
        header: t("inventory.col_product", "Produit"),
        cell: (p) => (
          <span className="flex items-center gap-2">
            <PackageX className="h-4 w-4 shrink-0 text-red-400" />
            <span>
              <span className="block text-sm font-medium text-stone-800">
                {p.name}
              </span>
              <span className="block text-xs text-stone-500">{p.brand}</span>
            </span>
          </span>
        ),
      },
      {
        id: "sku",
        header: t("inventory.col_sku", "SKU"),
        mobileVisible: false,
        cell: (p) => (
          <span className="font-mono text-xs text-stone-600">{p.sku}</span>
        ),
      },
      {
        id: "category",
        header: t("inventory.col_category", "Catégorie"),
        mobileVisible: false,
        cell: (p) => (
          <span className="text-sm text-stone-600">{p.category}</span>
        ),
      },
      {
        id: "updated_at",
        header: t("inventory.col_last_updated", "Dernière mise à jour"),
        mobileVisible: false,
        cell: (p) => (
          <span className="text-xs text-stone-400">
            {format(new Date(p.updated_at), "dd MMM yyyy", { locale: fr })}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        cell: (p) => (
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleRestock(p)}
            className="gap-1.5 text-xs"
          >
            <PackagePlus className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">
              {t("inventory.restock_action", "Réapprovisionner")}
            </span>
          </Button>
        ),
      },
    ],
    [t, handleRestock],
  );

  return (
    <div className="space-y-4">
      {/* Error */}
      {isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {t("inventory.error_loading", "Impossible de charger l'inventaire.")}
        </div>
      )}

      {/* Table */}
      {!isLoading && products.length === 0 ? (
        <EmptyState
          icon={<PackageX className="h-6 w-6" />}
          title={t("inventory.no_out_of_stock", "Aucune rupture de stock")}
          description={t(
            "inventory.no_out_of_stock_desc",
            "Tous vos produits ont du stock disponible.",
          )}
        />
      ) : (
        <AdminDataTable<InventoryProduct>
          columns={columns}
          data={products}
          isLoading={isLoading}
          pagination={pagination}
          onPageChange={setPage}
          emptyMessage={t("inventory.no_out_of_stock", "Aucune rupture")}
        />
      )}

      {/* Restock modal */}
      <RestockModal
        product={restockProduct}
        onClose={handleCloseRestock}
      />
    </div>
  );
}
